import { biomeBlendAt } from "./biomes";
import { CHUNK_SIZE_M } from "../simulation/constants";
import { hashSeed } from "../simulation/rng";
import type { BiomeId } from "../content/schemas";

export type TerrainSample = {
  height: number;
  moisture: number;
  /** Broad valley-carving signal. It never directly creates visible water. */
  drainage: number;
  lake: number;
  crater: number;
  waterMask: number;
  water: boolean;
  waterLevel: number | null;
  rock: number;
  soil: number;
  vegetation: number;
  wetSediment: number;
};

const LAKE_CELL_M = 620;
const CRATER_CELL_M = 310;

function hash01(seed: number, ix: number, iy: number): number {
  return hashSeed(seed, ix * 374761393 + iy * 668265263) / 4294967296;
}

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  if (edge0 === edge1) return value < edge0 ? 0 : 1;
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function valueNoise(seed: number, x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const u = fade(x - x0);
  const v = fade(y - y0);
  const n00 = hash01(seed, x0, y0);
  const n10 = hash01(seed, x0 + 1, y0);
  const n01 = hash01(seed, x0, y0 + 1);
  const n11 = hash01(seed, x0 + 1, y0 + 1);
  return n00 + (n10 - n00) * u + (n01 - n00) * v + (n11 - n10 - n01 + n00) * u * v;
}

function fbm(seed: number, x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let frequency = 1;
  for (let octave = 0; octave < octaves; octave += 1) {
    sum += amp * valueNoise(seed + octave * 1013, x * frequency, y * frequency);
    norm += amp;
    amp *= 0.5;
    frequency *= 2;
  }
  return sum / norm;
}

function warpedFbm(seed: number, x: number, y: number, scale: number, octaves: number): number {
  const warpX = (fbm(seed + 201, x * scale * 0.55, y * scale * 0.55, 2) - 0.5) * 150;
  const warpY = (fbm(seed + 407, x * scale * 0.55, y * scale * 0.55, 2) - 0.5) * 150;
  return fbm(seed, (x + warpX) * scale, (y + warpY) * scale, octaves);
}

function directionalRidge(
  seed: number,
  x: number,
  y: number,
  scale: number,
  angle: number,
): number {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const rx = x * cos - y * sin;
  const ry = x * sin + y * cos;
  const warp = (fbm(seed + 17, rx * scale * 0.42, ry * scale * 0.42, 2) - 0.5) * 190;
  const n = fbm(seed, (rx + warp) * scale, (ry - warp * 0.35) * scale * 0.48, 4);
  return 1 - Math.abs(n * 2 - 1);
}

type FeatureStamp = { amount: number; radius: number; waterLevel: number | null };

function featureStamp(
  seed: number,
  x: number,
  y: number,
  cellSize: number,
  chance: number,
  minRadius: number,
  maxRadius: number,
  withWaterLevel = false,
): FeatureStamp {
  const cellX = Math.floor(x / cellSize);
  const cellY = Math.floor(y / cellSize);
  let best: FeatureStamp = { amount: 0, radius: 0, waterLevel: null };
  for (let oy = -1; oy <= 1; oy += 1) {
    for (let ox = -1; ox <= 1; ox += 1) {
      const gx = cellX + ox;
      const gy = cellY + oy;
      if (hash01(seed, gx, gy) > chance) continue;
      const px = (gx + 0.16 + hash01(seed + 11, gx, gy) * 0.68) * cellSize;
      const py = (gy + 0.16 + hash01(seed + 29, gx, gy) * 0.68) * cellSize;
      const radius = minRadius + hash01(seed + 47, gx, gy) * (maxRadius - minRadius);
      const distance = Math.hypot(x - px, y - py);
      // A broad 22 m bank keeps flight gradients safe and comfortably exceeds
      // the 6 m minimum visual shoreline feather.
      const amount = 1 - smoothstep(radius - 22, radius, distance);
      if (amount > best.amount) {
        best = {
          amount,
          radius,
          waterLevel: withWaterLevel ? -1 + hash01(seed + 83, gx, gy) * 13 : null,
        };
      }
    }
  }
  return best;
}

function carrierFlatten(x: number, y: number): number {
  const distance = Math.hypot(x, y);
  if (distance >= 260) return 0;
  return 1 - smoothstep(86, 260, distance);
}

export function sampleHeight(seed: number, x: number, y: number): number {
  return sampleTerrain(seed, x, y).height;
}

export function sampleTerrain(seed: number, x: number, y: number): TerrainSample {
  // Four explicit spatial scales: continent, chained directional landforms,
  // rolling hills, and bounded surface relief. Biomes intentionally do not
  // modulate height: ownership boundaries remain purely material/ecology data.
  const continental = warpedFbm(seed + 3, x, y, 1 / 2300, 4);
  const continentalHeight = (continental - 0.5) * 28;
  const ridgeA = directionalRidge(seed + 91, x, y, 1 / 720, 0.38);
  const ridgeB = directionalRidge(seed + 127, x, y, 1 / 430, -0.82);
  const ridgeChain = Math.max(ridgeA * 0.92, ridgeB * 0.78);
  const valley = directionalRidge(seed + 151, x, y, 1 / 860, 1.18);
  const landform = Math.pow(ridgeChain, 2.8) * 47 - Math.pow(valley, 5) * 14;
  const rolling = (warpedFbm(seed + 191, x, y, 1 / 170, 3) - 0.5) * 26;
  const surface = (warpedFbm(seed + 233, x, y, 1 / 31, 2) - 0.5) * 5;
  const moisture = 0.12 + warpedFbm(seed + 53, x, y, 1 / 1800, 4) * 0.88;

  let height = 3 + continentalHeight + landform + rolling + surface;

  const drainageField = directionalRidge(seed + 307, x, y, 1 / 760, 0.72);
  const drainage = Math.pow(drainageField, 5) * smoothstep(0.3, 0.72, moisture);
  height -= drainage * (4.5 + ridgeChain * 7.5);

  const lakeStamp = featureStamp(seed + 601, x, y, LAKE_CELL_M, 0.2, 45, 110, true);
  let lake = lakeStamp.amount;
  let waterLevel = lake > 0 && lakeStamp.waterLevel != null ? lakeStamp.waterLevel : null;
  // Sparse feature cells become visible basins only in plausible lowlands;
  // a lake stamp never punches a cylindrical hole through a mountain ridge.
  const lowlandSuitability =
    waterLevel == null ? 0 : smoothstep(waterLevel + 14, waterLevel + 3, height);
  lake *= lowlandSuitability;
  let waterMask = lake;
  if (waterLevel != null) {
    const basinFloor = waterLevel - 1.8 - lake * 2.2;
    const basinBlend = lake * lake * (3 - 2 * lake);
    height = height * (1 - basinBlend) + Math.min(height, basinFloor) * basinBlend;
    waterMask *= smoothstep(waterLevel + 0.3, waterLevel - 1.2, height);
  }

  const craterStamp = featureStamp(seed + 809, x, y, CRATER_CELL_M, 0.28, 12, 32);
  const crater = craterStamp.amount;
  if (crater > 0) {
    const normalized = 1 - crater;
    const bowl = crater * crater * 2.4;
    const rim = Math.exp(-Math.pow((normalized - 0.2) / 0.12, 2)) * 1.15;
    height += rim - bowl;
  }

  const flatten = carrierFlatten(x, y);
  height = height * (1 - flatten) + 0.4 * flatten;
  if (Math.hypot(x, y) < 320) {
    lake = 0;
    waterMask = 0;
    waterLevel = null;
  }
  height = Math.max(-9, Math.min(110, height));

  const mountain = smoothstep(0.52, 0.78, continental);
  const rockRaw = 0.08 + Math.pow(ridgeChain, 2.4) * 0.78 + mountain * 0.22;
  const wetRaw = waterMask * 1.25 + drainage * 0.28;
  const vegetationRaw = moisture * (1 - Math.min(1, rockRaw)) * (1 - waterMask) * 0.92;
  const soilRaw = 0.55 + (1 - moisture) * 0.35 + crater * 0.2;
  const total = Math.max(1e-6, rockRaw + wetRaw + vegetationRaw + soilRaw);

  return {
    height,
    moisture,
    drainage,
    lake,
    crater,
    waterMask,
    water: waterMask > 0.5,
    waterLevel,
    rock: rockRaw / total,
    soil: soilRaw / total,
    vegetation: vegetationRaw / total,
    wetSediment: wetRaw / total,
  };
}

export function sampleClearance(seed: number, x: number, y: number, worldY: number): number {
  return worldY - sampleHeight(seed, x, y);
}

type Palette = { rock: number; soil: number; vegetation: number; wet: number };

const PALETTES: Record<BiomeId, Palette> = {
  ash_band: { rock: 0x66655e, soil: 0x7d6a50, vegetation: 0x526b46, wet: 0x4b5045 },
  rust_flats: { rock: 0x786a5d, soil: 0x9b6040, vegetation: 0x66733f, wet: 0x5e5143 },
  scrap_canyon: { rock: 0x626763, soil: 0x735f4d, vegetation: 0x49624d, wet: 0x454f49 },
};

/** Continuous material-weight preview color; the shader adds fine-scale response. */
export function terrainColor(seed: number, x: number, y: number, sample: TerrainSample): number {
  const blend = biomeBlendAt(seed, x / CHUNK_SIZE_M, y / CHUNK_SIZE_M);
  const primary = weightedPalette(PALETTES[blend.primary], sample);
  const secondary = weightedPalette(PALETTES[blend.secondary], sample);
  return mixHex(primary, secondary, blend.edge);
}

function weightedPalette(palette: Palette, sample: TerrainSample): number {
  const colors = [palette.rock, palette.soil, palette.vegetation, palette.wet];
  const weights = [sample.rock, sample.soil, sample.vegetation, sample.wetSediment];
  let red = 0;
  let green = 0;
  let blue = 0;
  for (let i = 0; i < colors.length; i += 1) {
    const color = colors[i]!;
    const weight = weights[i]!;
    red += ((color >> 16) & 255) * weight;
    green += ((color >> 8) & 255) * weight;
    blue += (color & 255) * weight;
  }
  return (Math.round(red) << 16) | (Math.round(green) << 8) | Math.round(blue);
}

function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  return (
    (Math.round(ar + (br - ar) * t) << 16) |
    (Math.round(ag + (bg - ag) * t) << 8) |
    Math.round(ab + (bb - ab) * t)
  );
}
